import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Cookies from "universal-cookie";
import queryString from "query-string";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DoughnutChart from "../components/common/Doughnut";
import AreaChart from "../components/common/AreaChart";
import { get, post } from "../services/api";
import {
  EmployeeEmptyState,
  EmployeeErrorState,
  EmployeeLoadingState,
  EmployeePageHeader,
  EmployeeSurface,
} from "../employee/components/EmployeePage";
import {
  AcknowledgementCard,
  CommentList,
  ReportSection,
} from "../employee/components/PerformanceReport";
import { tokens as t } from "../theme/tokens";

const chartColors = [
  "#B77816",
  "#0B9F84",
  "#B3402F",
  "#386E91",
  "#1C7A4E",
  "#7D5EA7",
  "#3478B8",
  "#068D6F",
];

const EmployeeSurveyResultBasic: React.FC = () => {
  const { employeeId, surveyId } = useParams();
  const parsedToken = queryString.parse(window.location.search).access_token;
  const publicToken = typeof parsedToken === "string" ? parsedToken : "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState<any[]>([]);
  const [employeeDetails, setEmployeeDetails] = useState<any>({});
  const [reviewPeriod, setReviewPeriod] = useState<any[]>([]);
  const [surveyAck, setSurveyAck] = useState<{ date: string | null; comment: string }>({ date: null, comment: "" });

  const managerView = useMemo(() => {
    if (!publicToken) return false;
    try {
      return (jwtDecode(publicToken) as any).linkType === "viewSurveyResultsManager";
    } catch (_error) {
      return false;
    }
  }, [publicToken]);

  useEffect(() => {
    if (publicToken) localStorage.setItem("publicToken", publicToken);
    const cookieName = process.env.REACT_APP_PUBLIC_COOKIE_NAME || "";
    if (cookieName && publicToken) {
      new Cookies().set(cookieName, publicToken, { domain: ".paperlessnow.ca", path: "/surveyResultsBasic" });
    }
  }, [publicToken]);

  useEffect(() => {
    if (!employeeId || !surveyId || !publicToken) {
      setError("This performance-report link is incomplete or has expired. Please contact your administrator for a fresh link.");
      setLoading(false);
      return;
    }

    setLoading(true);
    get(`survey/getServices?type=getSurveyResultAck&surveyId=${surveyId}&access_token=${publicToken}`)
      .then((response: any) => {
        const acknowledgement = response?.payload?.data?.[0] || {};
        setSurveyAck({
          date: acknowledgement.ack_date || null,
          comment: acknowledgement.ack_comments || "No comments provided",
        });
      })
      .catch(() => undefined);

    Promise.all([
      get(`openEmployee/${employeeId}`, publicToken),
      get(`survey/getServices?type=getSurveyQuestionsWithHistoricalData&employeeId=${employeeId}&surveyId=${surveyId}&access_token=${publicToken}`),
    ])
      .then(([employeeResponse, reportResponse]: any[]) => {
        setEmployeeDetails(employeeResponse?.payload?.data || {});
        setData(reportResponse?.payload?.data || []);
        setReviewPeriod(reportResponse?.payload?.reviewPeriod || []);
      })
      .catch(() => setError("We could not open this performance report. The link may have expired; please contact your administrator."))
      .finally(() => setLoading(false));
  }, [employeeId, publicToken, surveyId]);

  const currentRatings = useMemo(
    () => (data[0]?.result || []).filter((item: any) => ["Rank", "YesNo"].includes(item.surveyquestiontypename)),
    [data]
  );

  const ratingDifferences = useMemo(
    () =>
      currentRatings.map((rating: any) => {
        const previous = (data[1]?.result || []).find(
          (item: any) => item.surveyquestionheadingname === rating.surveyquestionheadingname
        );
        return previous ? Number(rating.AnswerValueAverage) - Number(previous.AnswerValueAverage) : null;
      }),
    [currentRatings, data]
  );

  const { trendData, comments } = useMemo(() => {
    const grouped: Record<string, { dates: string[]; values: number[] }> = {};
    const allComments: string[] = [];

    data.forEach((period) => {
      (period.result || []).forEach((item: any) => {
        if (item.surveyquestiontypename === "Comment" && item.AnswerText) allComments.unshift(item.AnswerText);
        if (["Rank", "YesNo"].includes(item.surveyquestiontypename)) {
          if (!grouped[item.surveyquestionheadingname]) grouped[item.surveyquestionheadingname] = { dates: [], values: [] };
          grouped[item.surveyquestionheadingname].dates.unshift(period.date);
          grouped[item.surveyquestionheadingname].values.unshift(Number(item.AnswerValueAverage));
        }
      });
    });

    return {
      comments: allComments,
      trendData: Object.entries(grouped).map(([name, values]) => ({
        name,
        series: [{ name, data: values.values }],
        dates: values.dates,
      })),
    };
  }, [data]);

  if (loading) return <EmployeeLoadingState message="Loading your performance report…" />;
  if (error) return <EmployeeErrorState message={error} />;

  const employeeName = [employeeDetails.first_name, employeeDetails.last_name].filter(Boolean).join(" ");

  return (
    <Stack spacing={3}>
      <EmployeePageHeader
        eyebrow="Performance report"
        title={employeeName || "Employee performance report"}
        description="How your management team rated you last month."
        action={reviewPeriod[0]?.review_period ? <Chip label={reviewPeriod[0].review_period} color="primary" variant="outlined" /> : undefined}
      />

      <ReportSection title="Current ratings" description="Scores use the PaperlessNOW five-point monthly employee-review scale; scores above three are above average.">
        {currentRatings.length ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
            {currentRatings.map((rating: any, index: number) => {
              const score = Number(rating.AnswerValueAverage);
              const difference = ratingDifferences[index];
              const color = chartColors[index % chartColors.length];
              return (
                <Paper key={`${rating.surveyquestionheadingname}-${index}`} variant="outlined" sx={{ borderColor: t.color.cardLine, p: 2, textAlign: "center" }}>
                  <Typography component="h3" sx={{ fontWeight: 600, minHeight: 42 }}>{rating.surveyquestionheadingname}</Typography>
                  <Box sx={{ position: "relative", maxWidth: 260, mx: "auto" }}>
                    <DoughnutChart data={[score, Math.max(0, 5 - score)]} labels={[rating.surveyquestionheadingname, "Remaining"]} colors={[color, t.color.rowLine]} />
                    <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
                      <Box sx={{ mt: 0.5 }}>
                        <Typography sx={{ fontSize: "2rem", fontWeight: 600, lineHeight: 1 }}>{score}</Typography>
                        {difference !== null && (
                          <Typography variant="body2" sx={{ mt: 0.75, color: difference < 0 ? t.color.bad : t.color.good, fontWeight: 600 }}>
                            {difference > 0 ? "▲" : difference < 0 ? "▼" : "—"} {Math.abs(difference).toFixed(1)} from prior
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <EmployeeEmptyState title="No current ratings are available" />
        )}
      </ReportSection>

      <ReportSection title="Performance trends" description="How your ratings have changed over time.">
        {trendData.length ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }, gap: 2 }}>
            {trendData.map((trend, index) => (
              <Paper key={trend.name} variant="outlined" sx={{ borderColor: t.color.cardLine, p: 2 }}>
                <Typography component="h3" sx={{ textAlign: "center", fontWeight: 600 }}>{trend.name}</Typography>
                <Typography variant="overline" component="div" color="text.secondary" sx={{ textAlign: "center", mt: 0.5 }}>Average</Typography>
                <Typography sx={{ textAlign: "center", fontSize: "2rem", fontWeight: 600 }}>
                  {currentRatings[index]?.AnswerValueAverage ?? trend.series[0].data[trend.series[0].data.length - 1]}
                </Typography>
                <AreaChart seriesData={trend.series} xaxisCategories={[...trend.dates]} color={[chartColors[index % chartColors.length]]} />
              </Paper>
            ))}
          </Box>
        ) : (
          <EmployeeEmptyState title="No historical ratings are available" />
        )}
      </ReportSection>

      <ReportSection title="Management comments" description="Written notes supplied with your ratings, if provided.">
        <CommentList comments={comments} />
      </ReportSection>

      <EmployeeSurface>
        <Alert severity="info" variant="outlined" sx={{ border: 0, borderRadius: 0 }}>
          Questions about a rating or comment should go to your management team. For help with PaperlessNOW, email <a href="mailto:hello@paperlessnow.ca">hello@paperlessnow.ca</a>.
        </Alert>
      </EmployeeSurface>

      <AcknowledgementCard
        date={surveyAck.date}
        comment={surveyAck.comment}
        managerView={managerView}
        onSubmit={(commentsValue) =>
          post("survey/postServices", {
            type: "saveSurveyResultsAck",
            comments: commentsValue || null,
            surveyId,
          })
        }
      />
    </Stack>
  );
};

export default EmployeeSurveyResultBasic;
